import { FILE_SAVE_FOLDER } from '@/constants/apiConstants';
import type { ActionData, ProjectData } from '@/types';
import type { KieStoryTTSData, TTSAudioData, TTSAudioMeta } from '@/types/online-data';
import { resolveAppStaticPath } from './staticAsset';
import { buildTTSAudioSplits } from './ttsSplitTiming';

/**
 * 将相对路径解析为绝对路径
 * @param baseUrl 基准 URL（如当前页面的绝对路径）
 * @param url 相对或绝对 URL
 * @returns 解析后的绝对 URL
 */
export function getAbsolutePath(baseUrl: string, url: string): string {
  // 以 ./ 开头：相对于 baseUrl 的路径，去掉 ./ 后拼接
  if (url.startsWith('./')) {
    return baseUrl + url.slice(1);
  }
  // 以 resources/ 开头：项目资源相对路径
  if (url.startsWith('resources/')) {
    return baseUrl + '/' + url;
  }
  return new URL(url, baseUrl).href;
}

/**
 * 将绝对 URL 转为相对于 baseUrl 的相对路径
 * 跨域时直接返回原绝对 URL
 * @param baseUrl 基准 URL
 * @param url 绝对 URL
 * @returns 相对路径
 */
export function getRelativePath(baseUrl: string, url: string): string {
  const base = new URL(baseUrl);
  const target = new URL(url, baseUrl);

  // 不同域 — 无法计算相对路径，直接返回绝对 URL
  if (base.origin !== target.origin) {
    return target.href;
  }

  const baseIsDir = base.pathname.endsWith('/');
  const baseParts = base.pathname.replace(/\/$/, '').split('/');
  const targetParts = target.pathname.replace(/\/$/, '').split('/');

  // 找到公共前缀长度
  let commonLen = 0;
  const minLen = Math.min(baseParts.length, targetParts.length);
  while (commonLen < minLen && baseParts[commonLen] === targetParts[commonLen]) {
    commonLen++;
  }

  // baseUrl 的目录深度（文件则取其所在目录）
  const baseDirLen = baseIsDir ? baseParts.length : baseParts.length - 1;
  const upCount = Math.max(0, baseDirLen - commonLen);
  const downParts = targetParts.slice(commonLen);

  const path = [...Array(upCount).fill('..'), ...downParts].join('/') || '.';
  return path + target.search + target.hash;
}

/**
 * 遍历项目中所有元素，用相对路径更新绝对路径：
 * - relativePath → 保留原始相对路径
 * - content      → 由 relativePath + baseUrl 解析为绝对路径
 * 与 restoreRelativePaths 互为逆操作
 */
export function resolveProjectPaths(projectData: ProjectData, baseUrl?: string): void {
  if (!baseUrl) return;

  // 将 book 所有页的 actions 与 bgm 的 action 平铺为一个数组
  const allActions = [
    ...(projectData.book?.pages?.flatMap((p) => p.actions ?? []) ?? []),
    ...(projectData.bgm ? [projectData.bgm] : [])
  ];

  // http(s)/协议相对 → 已是绝对地址；以 / 开头 → 应用静态资源，按 FontManager 规则解析（阅读端跨域）
  const isHttpUrl = (path: string): boolean => /^(https?:)?\/\//.test(path);
  const isAppStaticPath = (path: string): boolean => path.startsWith('/');
  const isRelativePath = (path: string): boolean => !isHttpUrl(path) && !isAppStaticPath(path);

  for (const action of allActions) {
    const el = action.element;
    if (!el || typeof el.content !== 'string' || !el.content) continue;

    // 优先取 relativePath，没有则取 content
    const sourcePath = el.relativePath || el.content;
    if (!sourcePath) continue;

    // 已是 http(s) 绝对地址则不动
    if (isHttpUrl(sourcePath)) continue;

    // 应用内置静态资源（如尾页 Vite 打包路径）：拼工作台/阅读端静态基路径，不拼书本 baseUrl
    if (isAppStaticPath(sourcePath)) {
      el.relativePath = sourcePath;
      el.content = resolveAppStaticPath(sourcePath);
      continue;
    }

    if (!isRelativePath(sourcePath)) continue;

    el.relativePath = sourcePath;
    el.content = getAbsolutePath(baseUrl, sourcePath);
  }
}

export interface ReplaceRecord {
  newFileList: Record<string, string[]>;
  oldFileList: Record<string, string[]>;
}

/**
 * 从 fileOptions 构建文件替换记录
 * @param fileOptions 文件修改记录 Map：id → { savePath, removePath }
 * @returns 替换记录
 */
export function buildReplaceRecord(
  fileOptions: Map<string, { savePath: string; removePath: string }>
): ReplaceRecord {
  const newFileList: Record<string, string[]> = {};
  const oldFileList: Record<string, string[]> = {};

  fileOptions.forEach(({ savePath, removePath }) => {
    if (savePath) {
      if (!newFileList[FILE_SAVE_FOLDER]) newFileList[FILE_SAVE_FOLDER] = [];
      if (!newFileList[FILE_SAVE_FOLDER].includes(savePath)) {
        newFileList[FILE_SAVE_FOLDER].push(savePath);
      }
    }

    if (removePath) {
      // 去除开头的 ./，避免 folder 计算出 ./ 前缀
      const normalizedPath = removePath.replace(/^\.\//, '');
      const lastSlash = normalizedPath.lastIndexOf('/');
      const folder = lastSlash > 0 ? normalizedPath.substring(0, lastSlash) : '';
      const filename = lastSlash > 0 ? normalizedPath.substring(lastSlash + 1) : normalizedPath;
      if (!oldFileList[folder]) oldFileList[folder] = [];
      if (!oldFileList[folder].includes(filename)) {
        oldFileList[folder].push(filename);
      }
    }
  });

  return { newFileList, oldFileList };
}

/**
 * 保存前还原：将图片元素的 content（绝对路径）替换回 relativePath（相对路径）
 * 与 resolveImagePaths 互为逆操作
 * - content 以 baseUrl 开头 → 还原为相对路径
 * - content 不以 baseUrl 开头 → 改为 resources/extfile/文件名
 */
export function restoreRelativePaths(actions: ActionData[], baseUrl?: string): void {
  for (const action of actions) {
    const el = action.element;
    if (!el || typeof el.content !== 'string' || !el.content) continue;

    // 尾页本地图：还原为根相对应用路径，不写入 resources/extfile
    if (el.config?.tailPageRole?.startsWith('asset-')) {
      const sourcePath = el.relativePath || el.content;
      try {
        const pathname = new URL(sourcePath, 'https://local.invalid').pathname;
        el.content = pathname;
        el.relativePath = pathname;
      } catch {
        el.content = sourcePath;
      }
      continue;
    }

    if (baseUrl && el.content.startsWith(baseUrl)) {
      // 以 baseUrl 开头 → 还原为相对路径
      if (!el.relativePath) {
        el.relativePath = getRelativePath(baseUrl, el.content);
      }
      el.content = el.relativePath;
    } else {
      // 非 baseUrl 的绝对路径 → 统一改为 resources/extfile/文件名
      const sourcePath = new URL(el.content).pathname;
      const lastSlash = sourcePath.lastIndexOf('/');
      const filename = lastSlash > 0 ? sourcePath.substring(lastSlash + 1) : sourcePath;

      el.relativePath = `${FILE_SAVE_FOLDER}/${filename}`;
      el.content = el.relativePath;
    }
  }
}

/**
 * 收集被替换/新上传的图片元素信息，并调用 restoreRelativePaths 统一处理路径。
 * @returns 替换记录
 */
export function collectReplacedFiles(actions: ActionData[], baseUrl?: string): ReplaceRecord {
  const newFileList: Record<string, string[]> = {};
  const oldFileList: Record<string, string[]> = {};

  for (const action of actions) {
    const el = action.element;
    if (!el || typeof el.content !== 'string' || !el.content) continue;

    // content 以 baseUrl 开头 → 不记录
    if (baseUrl && el.content.startsWith(baseUrl)) continue;

    // 非 baseUrl 的绝对路径 → 记录替换信息
    const sourcePath = el.relativePath || new URL(el.content).pathname;
    const lastSlash = sourcePath.lastIndexOf('/');
    const folder = lastSlash > 0 ? sourcePath.substring(0, lastSlash) : '';
    const filename = lastSlash > 0 ? sourcePath.substring(lastSlash + 1) : sourcePath;

    if (!newFileList[FILE_SAVE_FOLDER]) newFileList[FILE_SAVE_FOLDER] = [];
    newFileList[FILE_SAVE_FOLDER].push(el.content);
    if (!oldFileList[folder]) oldFileList[folder] = [];
    oldFileList[folder].push(filename);
  }

  // 统一处理路径：baseUrl 元素还原相对路径，其余改为 resources/extfile/
  restoreRelativePaths(actions, baseUrl);

  return { newFileList, oldFileList };
}

/**
 * 将 KieStoryTTSData 转换为 TTSAudioMeta，封面和内页分开返回
 * @param ttsData Kie 故事 TTS 数据
 * @returns { cover: TTSAudioMeta | null, list: TTSAudioMeta[] }
 */
export function getTTSAudioMetaList(ttsData: KieStoryTTSData | null): {
  cover: TTSAudioMeta | null;
  list: TTSAudioMeta[];
} {
  if (!ttsData) return { cover: null, list: [] };

  const {
    audio,
    roleId,
    source,
    name = '角色',
    description = '普通声音',
    portrait = '',
    category = '通用',
    speed = 1,
    volume = 100
  } = ttsData;

  // 封面音频已并入 audio 数组，通过 page === 0 识别
  const coverItem = (audio ?? []).find((item) => item.page === 0) ?? null;

  const coverMeta: TTSAudioMeta | null = coverItem
    ? {
        name,
        description,
        portrait,
        category,
        roleId,
        source,
        volume,
        speed,
        text: coverItem.text,
        url: coverItem.url,
        duration: coverItem.duration,
        splits: buildTTSAudioSplits(coverItem)
      }
    : null;

  // 内页音频排除封面（page > 0）
  const list: TTSAudioMeta[] = (audio ?? [])
    .filter((item) => item.page !== 0)
    .map((item) => ({
      name,
      description,
      portrait,
      category,
      roleId,
      source,
      speed,
      volume,
      text: item.text,
      url: item.url,
      duration: item.duration,
      splits: buildTTSAudioSplits(item)
    }));

  return { cover: coverMeta, list };
}

/** 为已保存的 project 补齐最新 TTS 分词时间数据，不改变页面与音频的原有映射。 */
export function hydrateProjectTTSAudioSplits(
  project: ProjectData | null,
  ttsData: KieStoryTTSData | null
): void {
  const pages = project?.book?.pages;
  if (!pages?.length || !ttsData?.audio?.length) return;

  const audioByPage = new Map<number, TTSAudioData>(
    ttsData.audio.map((audio) => [audio.page, audio])
  );

  pages.forEach((page) => {
    if (!page.tts) return;

    const pageNumber = page.originIndex ?? page.index;
    let audio = pageNumber === undefined ? undefined : audioByPage.get(pageNumber);

    if (!audio && page.tts.url) {
      audio = ttsData.audio.find((item) => item.url === page.tts?.url);
    }

    if (audio) {
      page.tts.splits = buildTTSAudioSplits({
        ...audio,
        duration: audio.duration > 0 ? audio.duration : (page.tts.duration ?? 0)
      });
    }
  });
}
